/**
 * The Playbook, inside the Team card: the live rules, each with its "applies
 * to" scope; Adopt or Dismiss on a rule the Team proposed for itself (Dismiss
 * is the veto, because a superseded rule is what the concession loop's
 * rejection memory keys on); Retire on a rule a person wrote; and one box to
 * add a rule. An inherited workspace rule reads here but cannot be changed
 * from a Team. Every write lands at once, outside the sheet's Save, because a
 * rule is a record the Team keeps, not a setting it holds.
 */
import { useEffect, useState } from "react";

import { RuleApplies } from "../../desks/RuleScopeEditor.js";
import { readJsonError } from "../../lib/readJsonError.js";
import { Btn } from "../../queue/index.js";
import {
  PLAYBOOK_ADD,
  PLAYBOOK_ADD_PLACEHOLDER,
  PLAYBOOK_ADOPT,
  PLAYBOOK_DISMISS,
  PLAYBOOK_EMPTY,
  PLAYBOOK_OVERRIDES,
  PLAYBOOK_PROPOSED_HINT,
  PLAYBOOK_RETIRE,
  PLAYBOOK_WORKSPACE_HINT,
} from "../copy.js";
import type { PlaybookRule } from "../useTeamPage.js";
import { Field } from "./fields.js";

export interface PlaybookRulesProps {
  deskId: string;
  rules: readonly PlaybookRule[];
  canEdit: boolean;
  onChanged: () => Promise<void> | void;
}

export function PlaybookRules({ deskId, rules: initial, canEdit, onChanged }: PlaybookRulesProps) {
  const [rules, setRules] = useState<PlaybookRule[]>(() => [...initial]);
  const [newRule, setNewRule] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setRules([...initial]);
  }, [initial]);

  const base = `/api/desks/${encodeURIComponent(deskId)}/playbook`;

  const write = async (path: string, init: RequestInit, apply: (body: unknown) => void): Promise<boolean> => {
    setBusy(true);
    setError(null);
    try {
      const res = await fetch(path, { credentials: "include", ...init });
      if (!res.ok) {
        setError(await readJsonError(res));
        return false;
      }
      apply(await res.json().catch(() => null));
      await onChanged();
      return true;
    } catch {
      setError("Could not reach the server. Try again.");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const add = async () => {
    const ruleText = newRule.trim();
    if (!ruleText) return;
    const landed = await write(
      base,
      { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ ruleText }) },
      (body) => {
        const rule = (body as { rule?: PlaybookRule } | null)?.rule;
        if (rule) setRules((prev) => [...prev, rule]);
      },
    );
    if (landed) setNewRule("");
  };

  const act = (ruleId: string, verb: "confirm" | "dismiss" | "deprecate") =>
    write(`${base}/${encodeURIComponent(ruleId)}/${verb}`, { method: "POST" }, () =>
      setRules((prev) =>
        verb === "confirm"
          ? prev.map((rule) => (rule.id === ruleId ? { ...rule, origin: "human" as const } : rule))
          : prev.filter((rule) => rule.id !== ruleId),
      ),
    );

  return (
    <>
      <div className="checklist" data-testid="settings-playbook">
        {rules.length === 0 && <p className="fact" style={{ padding: "4px 8px" }}>{PLAYBOOK_EMPTY}</p>}
        {rules.map((rule) => {
          const proposed = rule.origin === "proposed";
          const workspace = rule.tier === "workspace";
          return (
            <div key={rule.id} className={proposed ? "rule proposed" : "rule"} data-testid={`settings-rule-${rule.id}`}>
              <span className="d2">
                {rule.ruleText}
                {rule.overridesRuleId != null && (
                  <span className="fact" style={{ display: "block", fontSize: 11 }}>
                    {PLAYBOOK_OVERRIDES}
                    {rule.overridesRuleText ? `: "${rule.overridesRuleText}"` : ""}
                  </span>
                )}
              </span>
              <span className="d" title={workspace ? PLAYBOOK_WORKSPACE_HINT : proposed ? PLAYBOOK_PROPOSED_HINT : undefined}>
                {workspace ? "workspace" : proposed ? "proposed" : "this Team"}
              </span>
              {!workspace && canEdit && (
                <>
                  <RuleApplies
                    deskId={deskId}
                    ruleId={rule.id}
                    scope={rule.scope ?? null}
                    onSaved={({ oldRuleId, newRuleId, scope }) => {
                      setRules((prev) =>
                        prev.map((entry) => (entry.id === oldRuleId ? { ...entry, id: newRuleId, scope } : entry)),
                      );
                      void onChanged();
                    }}
                  />
                  {proposed ? (
                    <>
                      <button type="button" className="ref" disabled={busy} data-testid={`settings-rule-adopt-${rule.id}`} onClick={() => void act(rule.id, "confirm")}>
                        {PLAYBOOK_ADOPT}
                      </button>
                      <button type="button" className="ref quiet" disabled={busy} data-testid={`settings-rule-dismiss-${rule.id}`} onClick={() => void act(rule.id, "dismiss")}>
                        {PLAYBOOK_DISMISS}
                      </button>
                    </>
                  ) : (
                    <Btn variant="quiet" size="sm" disabled={busy} data-testid={`settings-rule-retire-${rule.id}`} onClick={() => void act(rule.id, "deprecate")}>
                      {PLAYBOOK_RETIRE}
                    </Btn>
                  )}
                </>
              )}
            </div>
          );
        })}
      </div>
      {canEdit && (
        <Field label="Add a rule">
          <div className="inline">
            <span className="in">
              <input
                value={newRule}
                maxLength={500}
                placeholder={PLAYBOOK_ADD_PLACEHOLDER}
                aria-label={PLAYBOOK_ADD_PLACEHOLDER}
                data-testid="settings-rule-add-input"
                onChange={(event) => setNewRule(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void add();
                  }
                }}
              />
            </span>
            <Btn variant="secondary" size="sm" disabled={busy || !newRule.trim()} data-testid="settings-rule-add" onClick={() => void add()}>
              {PLAYBOOK_ADD}
            </Btn>
          </div>
        </Field>
      )}
      {error && (
        <p className="fact" role="alert" style={{ color: "var(--incident)" }}>
          {error}
        </p>
      )}
    </>
  );
}
